import { useCallback, useEffect, useState } from "react";
import { useLatestRequest } from "../../../hooks/useLatestRequest";
import { useResultHandler } from "../../../hooks/useResultHandler";
import { getNewsList } from "../api/news.api";
import type {
  NewsListItemResponse,
  NewsSortDirection,
  NewsSortKey,
} from "../types/news.types";

const ITEMS_PER_PAGE = 20;

export interface NewsListFilters {
  keyword?: string;
}

const EMPTY_FILTERS: NewsListFilters = {};

export const useGetNewsList = () => {
  const [news, setNews] = useState<NewsListItemResponse[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<NewsListFilters>(EMPTY_FILTERS);
  const [debouncedFilters, setDebouncedFilters] = useState<NewsListFilters>(EMPTY_FILTERS);
  const [sorting, setSorting] = useState<{
    key: NewsSortKey | null;
    direction: NewsSortDirection | null;
  }>({ key: "created_at", direction: "desc" });
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const { run: runLatestRequest, loading } = useLatestRequest();
  const { handleError } = useResultHandler();

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedFilters(filters);
      setPage(1);
    }, 300);

    return () => window.clearTimeout(timer);
  }, [filters]);

  const fetchNews = useCallback(async () => {
    try {
      setError(null);
      const response = await runLatestRequest((signal) =>
        getNewsList(
          {
            pagination: {
              index: page - 1,
              size: ITEMS_PER_PAGE,
            },
            keyword: debouncedFilters.keyword?.trim() || undefined,
            sorting: {
              sort_key: sorting.key ?? "created_at",
              sort_direction: sorting.direction ?? "desc",
            },
          },
          signal,
        ),
      );

      if (!response) return;

      setNews([...response.list]);
      setTotalCount(response.paging.total);
    } catch (fetchError) {
      setNews([]);
      setTotalCount(0);
      setError(handleError(fetchError, "Failed to load news"));
    }
  }, [debouncedFilters, handleError, page, runLatestRequest, sorting]);

  useEffect(() => {
    void fetchNews();
  }, [fetchNews]);

  const updateFilter = useCallback((key: keyof NewsListFilters, value?: string) => {
    setFilters((current) => {
      if (!value) {
        const { [key]: _removed, ...remainingFilters } = current;
        return remainingFilters;
      }

      return { ...current, [key]: value };
    });
  }, []);

  const updateSorting = useCallback(
    (key: NewsSortKey, direction: NewsSortDirection | null) => {
      setSorting({ key: direction ? key : null, direction });
      setPage(1);
    },
    [],
  );

  return {
    news,
    loading,
    error,
    filters,
    setFilters,
    updateFilter,
    sorting,
    updateSorting,
    page,
    setPage,
    totalCount,
    itemsPerPage: ITEMS_PER_PAGE,
    refreshNews: fetchNews,
  };
};
